import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchNuts, createNut, deleteNut, updateNut } from '../../common/jsonstore';
import { Nut } from '../../common/model';
import { AppState } from '../../redux/store/rootReducer';
import { AppDispatch } from '../../redux/store/store';
import { isTokenExpired } from '../../services/authenticationService';
import { signOut } from '../../services/localStorage';
import { handleError } from '../../services/messageApiService';

export interface IAuthentication {
  Nut: Nut[];
  success: any;
  isProcessingComplete: boolean;
}

const initialState: IAuthentication = { Nut: [], success: {}, isProcessingComplete: false };

const NutSlice = createSlice({
  name: 'Nuts',
  initialState,
  reducers: {
    receiveNut: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        Nut: action.payload,
        isProcessingComplete: false
      };
    },
    success: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        success: action.payload,
        isProcessingComplete: true
      };
    }
  }
});


export const loadNuts = (): any => async (dispatch: AppDispatch) => {
  if (isTokenExpired()) {
    const Nuts = await fetchNuts().then((res: any) => {
      dispatch(NutSlice.actions.receiveNut(res))
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Nut data not loaded';
        handleError(msg);
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const addNut = (newNut: Nut): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (isTokenExpired()) {
    const res = await createNut(newNut).then((resp: any) => {
      dispatch(NutSlice.actions.receiveNut([...getState().Nuts.Nut, resp.data[0]]));
      dispatch(NutSlice.actions.success({ 'sts': true, data: resp?.msg }));
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Nut data not added';
        dispatch(NutSlice.actions.success({ 'sts': false, data: msg }));
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const modifyNut = (newNut: Nut, id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (isTokenExpired()) {
    const res = await updateNut(newNut, id).then((resp: any) => {
      const updatedFittingSizes = getState().Nuts.Nut.map(el => (el.id === id ? resp.data[0] : el))
      dispatch(NutSlice.actions.receiveNut(updatedFittingSizes));
      dispatch(NutSlice.actions.success({ 'sts': true, data: resp?.msg }));
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Nut data not updated';
        dispatch(NutSlice.actions.success({ 'sts': false, data: msg }));
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const removeNut = (id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (isTokenExpired()) {
    deleteNut(id).then((res: any) => {
      const updatedNuts = getState().Nuts.Nut.filter((item) => item.id !== id);
      dispatch(NutSlice.actions.receiveNut(updatedNuts));
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Nut data not deleted';
        handleError(msg);
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const updateSts = (data: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  dispatch(NutSlice.actions.success(data));
}

export default NutSlice.reducer;
